// Framework and third-party non-ui
import React, { Component } from "react";

// Redux operations and local helpers/utils/modules
import { connect } from "react-redux";
import Rdx from "../../../redux/Rdx";
import Context from "../../../context/Context";
import { ModalController } from "../../../common/components/Modal";
import Topic from "../../../context/Topic";
import FieldNames from "../../../aiim/datasets/FieldNames";
import OfficePlan from "../../base/OfficePlan";
import TransactionGuard from "../../base/TransactionGuard";
import * as aiimUtil from "../../../aiim/util/aiimUtil";
import * as component from "../../../components/util/component";
import * as officePlanUtil from "../../base/officePlanUtil";
import * as queryUtil from "../../base/queryUtil";

// Component specific modules (Component-styled, etc.)
import { AssignToContainer } from "../../styles/Common/commonStyles";
import { ComboButton, Button } from "../../styles/Common/peopleAssignTo";

// App components
import SelectArea from "./SelectArea";
import SelectPerson from "./SelectPerson";
import UnassignPrompt from "./UnassignPrompt";

// Third-party components (buttons, icons, etc.)
import Menu, { MenuItem } from "calcite-react/Menu";
import PeopleIcon from "calcite-ui-icons-react/UsersIcon";
import HotDeskIcon from "calcite-ui-icons-react/DeskIcon";
import HotelIcon from "calcite-ui-icons-react/ServicesIcon";
import UnassignIcon from "calcite-ui-icons-react/MinusCircleIcon";
import ReactTooltip from "react-tooltip";

// JSON

// CSS

class UnitsAssignTo extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isButtons: this.props.buttons !== undefined ? this.props.buttons : false,
      name: this.props.name || ""
    };
  }

  componentDidUpdate = () => {
    ReactTooltip.rebuild();
  };

  assignPersonToUnit = personFeatureItem => {
    const unitFeatureItems = this.getFeatureItems();
    if (!unitFeatureItems || unitFeatureItems.length !== 1) return;
    const unitFeatureItem = unitFeatureItems[0];
    let mod = false, guard;
    this.ensureUnitGeometry(unitFeatureItem)
      .then(() => {
        if (this.canAssign(this.getFeatureItems(), "assignToPerson")) {
          mod = true;
          guard = new TransactionGuard({featureItems: [personFeatureItem]});
          return officePlanUtil.assignPeopleToUnit(
            [personFeatureItem],
            unitFeatureItem
          );
        }
      })
      .then(result => {
        if (guard) guard.close();
        if (mod) {
          Topic.publish(Topic.PlanModified, {
            action: OfficePlan.Action_AssignmentsUpdated
          });
          this.onAssignment();
        }
      })
      .catch(error => {
        if (guard) guard.close();
        console.error("Error assigning person to unit", error);
        Topic.publishErrorUpdatingData(error.submessage);
      });
  };

  assignToClicked = (evt, option) => {
    const value = option.value;
    const caption = option.caption || "";
    let areaType = "";
    switch (value) {
      case "assignToPerson":
        this.assignToPersonClicked(caption);
        break;
      case "assignToHotDesk":
        areaType = OfficePlan.SpaceAssignmentTypes.hotdesk;
        this.assignToAreaClicked(caption, areaType);
        break;
      case "assignToHotel":
        areaType = OfficePlan.SpaceAssignmentTypes.hotel;
        this.assignToAreaClicked(caption, areaType);
        break;
      case "assignToNone":
        this.assignToNoneClicked(evt);
        break;
      default:
        console.log("no option for this selection available");
        break;
    }
  };

  assignToAreaClicked = (title, areaType, evt) => {
    if (evt) evt.stopPropagation();
    if (!this.canAssign(this.getFeatureItems())) return;
    SelectArea.showModal({
      title: title,
      areaType: areaType,
      promptOptions: {
        subject: "units",
        subjectItems: this.getFeatureItems(),
        target: areaType
      },
      onOK: sel => {
        const areaItem = sel;
        if (areaItem) {
          this.assignUnitsToArea(areaItem, areaType);
        }
      }
    });
  };

  assignToPersonClicked = (title, evt) => {
    if (evt) evt.stopPropagation();
    if (!this.canAssign(this.getFeatureItems(), "assignToPerson")) return;
    SelectPerson.showModal({
      title: title,
      promptOptions: {
        subject: "units",
        subjectItems: this.getFeatureItems(),
        target: "person"
      },
      onOK: sel => {
        let objectId = sel && sel.objectId;
        if (typeof objectId === "number") {
          const source = officePlanUtil.getPeopleSource();
          const featureItem = queryUtil.makeFeatureItem(source, sel.feature);
          this.assignPersonToUnit(featureItem);
        }
      }
    });
  };

  assignToNoneClicked = evt => {
    if (evt) evt.stopPropagation();
    if (!this.canAssign(this.getFeatureItems())) return;
    const i18n = Context.instance.i18n;
    let title = i18n.spaceplanner.unassignPrompt.removeAssignmentsTitle;
    const featureItems = this.getFeatureItems();
    let promptOptions = {
      subject: "units",
      subjectItems: featureItems,
    };
    if (featureItems.length === 1) {
      const featureItem = featureItems[0];
      if (!officePlanUtil.isUnitUnassigned(featureItem)) {
        let fromName;
        const attributes = featureItem.feature.attributes;
        const areaId = aiimUtil.getAttributeValue(attributes,FieldNames.UNITS_AREA_ID);
        const hasAreaId = (typeof areaId === "string" && areaId.length > 0);
        if (hasAreaId) {
          fromName = OfficePlan.getActive().getAreaName(areaId);
          promptOptions = {
            subject: "unit",
            subjectItem: featureItem,
            fromName: fromName
          };
        }
      }
    }

    const content = (
      <UnassignPrompt promptOptions={promptOptions} unitFeatureItems={featureItems}/>
    );
    const controller = new ModalController({});
    let okButton;
    let props = {
      title: title,
      showOKCancel: true,
      closeOnOK: true,
      okLabel: i18n.spaceplanner.unassign,
      onOK: () => {
        if (okButton) okButton.disable();
        this.unassignUnits();
      },
      onMountOKButton: btn => {
        okButton = btn;
      }
    };
    controller.show(props, content);
  };

  assignUnitsToArea = (areaItem, areaType) => {
    const unitFeatureItems = this.getFeatureItems();
    const guard = new TransactionGuard({featureItems: unitFeatureItems});
    officePlanUtil
      .assignUnitsToArea(unitFeatureItems, areaItem, areaType)
      .then(result => {
        guard.close();
        Topic.publish(Topic.PlanModified, {
          action: OfficePlan.Action_AssignmentsUpdated
        });
        this.onAssignment();
      })
      .catch(error => {
        guard.close();
        console.error("Error assigning unit to area", error);
        Topic.publishErrorUpdatingData(error.submessage);
      });
  };

  canAssign = (unitFeatureItems, type) => {
    if (type === "assignToPerson") {
      return unitFeatureItems && unitFeatureItems.length === 1;
    } else {
      return (
        unitFeatureItems &&
        unitFeatureItems.length > 0 &&
        unitFeatureItems.length <= this.getMaxFeatureItems()
      );
    }
  };

  canUnassign = featureItems => {
    if (!featureItems || featureItems.length === 0) {
      return false;
    } else if (this.props.disableUnassign) {
      return false;
    } else if (this.props.enableUnassign) {
      return true;
    }
    const nv = OfficePlan.SpaceAssignmentTypes.none;
    for (let i = 0; i < featureItems.length; i++) {
      const attributes = featureItems[i].feature.attributes;
      const areaId = aiimUtil.getAttributeValue(
        attributes,
        FieldNames.UNITS_AREA_ID
      );
      const spaceAssignment = aiimUtil.getAttributeValue(
        attributes,
        FieldNames.UNITS_SPACE_ASSIGNMENT_TYPE
      );
      if (areaId || spaceAssignment !== nv) return true;
    }
    return false;
  };

  // canUnassign = (featureItems) => {
  //   if (
  //     this.props.disableUnassign !== undefined
  //   ) {
  //     return this.props.disableUnassign;
  //   } else if (
  //     this.props.enableUnassign !== undefined
  //   ) {
  //     return this.props.enableUnassign;
  //   }
  //   if (!featureItems || featureItems.length === 0) {
  //     return false;
  //   }
  //   const nv = OfficePlan.SpaceAssignmentTypes.none;
  //   for (let i = 0; i < featureItems.length; i++) {
  //     const attributes = featureItems[i].feature.attributes;
  //     const areaId = aiimUtil.getAttributeValue(
  //       attributes,
  //       FieldNames.UNITS_AREA_ID
  //     );
  //     const spaceAssignment = aiimUtil.getAttributeValue(
  //       attributes,
  //       FieldNames.UNITS_SPACE_ASSIGNMENT_TYPE
  //     );
  //     if (areaId || spaceAssignment !== nv) return true;
  //   }
  //   return false;
  // };

  componentWillUnmount() {
    component.componentWillUnmount(this);
  }

  ensureUnitGeometry = unitFeatureItem => {
    const source = officePlanUtil.getUnitsSource();
    return queryUtil.ensureGeometry(source, unitFeatureItem);
  };

  getFeatureItems = () => {
    return this.props.featureItems;
  };

  getMaxFeatureItems = () => {
    return 1001;
  };

  onAssignment = () => {
    if (typeof this.props.onAssignment === "function") {
      this.props.onAssignment();
    }
  };

  renderButtons = () => {
    const i18n = Context.instance.i18n;
    const featureItems = this.getFeatureItems();
    const hasUnassignButton = this.canUnassign(featureItems);
    let captionKey = "captionSingular";
    if (featureItems && featureItems.length > 1) captionKey = "captionPlural";
    return (
      <>
        <Button
          onClick={evt =>
            this.assignToPersonClicked(
              i18n.spaceplanner.units.assignTo.person.captionSingular,
              evt
            )
          }
          icon={<PeopleIcon size={16} />}
          iconPosition="before"
          data-tip={i18n.spaceplanner.units.assignTo.person.label}
          data-for="genericTooltip"
        />
        <Button
          onClick={evt =>
            this.assignToAreaClicked(
              i18n.spaceplanner.units.assignTo.hotdesk[captionKey],
              OfficePlan.SpaceAssignmentTypes.hotdesk,
              evt
            )
          }
          icon={<HotDeskIcon size={16} />}
          iconPosition="before"
          data-tip={i18n.spaceplanner.units.assignTo.hotdesk.label}
          data-for="genericTooltip"
        />
        <Button
          onClick={evt =>
            this.assignToAreaClicked(
              i18n.spaceplanner.units.assignTo.hotel[captionKey],
              OfficePlan.SpaceAssignmentTypes.hotel,
              evt
            )
          }
          icon={<HotelIcon size={16} />}
          iconPosition="before"
          data-tip={i18n.spaceplanner.units.assignTo.hotel.label}
          data-for="genericTooltip"
        />
        {hasUnassignButton ? (
          <Button
            clear
            icon={<UnassignIcon size={16} />}
            iconPosition="before"
            onClick={evt => this.assignToNoneClicked(evt)}
            data-tip={i18n.spaceplanner.unassign}
            data-for="genericTooltip"
          />
        ) : null}
      </>
    );
  };

  renderCombo = () => {
    const i18n = Context.instance.i18n;
    const featureItems = this.getFeatureItems();
    const disabled = !this.canAssign(featureItems);
    const hasUnassignButton = this.canUnassign(featureItems);
    let captionKey = "captionSingular";
    if (featureItems && featureItems.length > 1) captionKey = "captionPlural";

    const options = [];
    if (this.canAssign(this.getFeatureItems(), "assignToPerson")) {
      options.push({
        label: i18n.spaceplanner.units.assignTo.person.label,
        caption: i18n.spaceplanner.units.assignTo.person.captionSingular,
        value: "assignToPerson"
      });
    }
    options.push({
      label: i18n.spaceplanner.units.assignTo.hotdesk.label,
      caption: i18n.spaceplanner.units.assignTo.hotdesk[captionKey],
      value: "assignToHotDesk"
    });
    options.push({
      label: i18n.spaceplanner.units.assignTo.hotel.label,
      caption: i18n.spaceplanner.units.assignTo.hotel[captionKey],
      value: "assignToHotel"
    });

    const menuItems = [];
    options.forEach(option => {
      menuItems.push(
        <MenuItem
          key={option.value}
          onClick={evt => this.assignToClicked(evt, option)}
        >
          {option.label}
        </MenuItem>
      );
    });

    return (
      <AssignToContainer noWrap={this.props.noWrap}>
        <ComboButton
          label={i18n.spaceplanner.units.assignTo.label}
          disabled={disabled}
          onClick={() =>
            this.canAssign(this.getFeatureItems(), "assignToPerson")
              ? this.assignToPersonClicked(
                  i18n.spaceplanner.units.assignTo.person.captionSingular
                )
              : this.assignToAreaClicked(
                  i18n.spaceplanner.units.assignTo.hotdesk[captionKey],
                  OfficePlan.SpaceAssignmentTypes.hotdesk
                )
          }
        >
          <Menu>{menuItems}</Menu>
        </ComboButton>
        {hasUnassignButton ? (
          <Button
            clear
            disabled={disabled}
            noWrap={this.props.noWrap}
            onClick={evt => this.assignToNoneClicked(evt)}
          >
            {i18n.spaceplanner.unassign}
          </Button>
        ) : null}
      </AssignToContainer>
    );
  };

  render() {
    const { isButtons } = this.state;
    return isButtons ? this.renderButtons() : this.renderCombo();
  }

  unassignUnits = () => {
    const featureItems = this.getFeatureItems();
    if (!this.canAssign(featureItems)) return;
    const guard = new TransactionGuard({featureItems: featureItems});
    officePlanUtil
      .unassignUnits(featureItems)
      .then(result => {
        guard.close();
        Topic.publish(Topic.PlanModified, {
          action: OfficePlan.Action_AssignmentsUpdated
        });
        this.onAssignment();
      })
      .catch(error => {
        guard.close();
        console.error("Error unassigning unit", error);
        Topic.publishErrorUpdatingData(error.submessage);
      });
  };
}

const mapStateToProps = (state, ownProps) => {
  const rdxFeatureItemsKey = ownProps.rdxFeatureItemsKey;
  if (rdxFeatureItemsKey && !ownProps.featureItems) {
    return { featureItems: Rdx.getValue(state, rdxFeatureItemsKey) };
  } else {
    return {};
  }
};

export default connect(mapStateToProps)(UnitsAssignTo);
